/** Parse “open X film/series in view|edit mode” commands (pure, no Angular). */

export type SlateOpenMode = 'view' | 'edit';
export type SlateOpenKind = 'film' | 'webseries' | 'unknown';

export interface SlateOpenCommand {
    mode: SlateOpenMode;
    kind: SlateOpenKind;
    title: string;
    /** Explicit catalog id from chip, e.g. (id:530) */
    contentId?: string;
}

export function isOpenContentCommand(text: string): boolean {
    const t = text.toLowerCase().trim();
    // How-to / nav help is not open-by-title
    if (
        /how\s+(?:do\s+i|to|can\s+i)|where\s+(?:is|can|do)|steps?\s+to|guide\s+me|show\s+me\s+how/.test(
            t
        )
    ) {
        return false;
    }
    // "view film list" / "open dashboard" / wallet — not content open
    if (
        /\b(?:film|movie|series)\s+list\b/.test(t) ||
        /\blist\s+(?:of\s+)?(?:films?|movies?|series)\b/.test(t) ||
        /\b(?:open|go\s+to|navigate\s+to)\s+(?:the\s+)?(?:dashboard|wallet|report|settings?|home)\b/.test(
            t
        )
    ) {
        return false;
    }
    const hasOpen =
        /\b(?:please\s+)?(?:open|go\s+to|navigate\s+to|take\s+me\s+to)\b/.test(
            t
        );
    const hasKind = /\b(film|movie|web\s*series|series)\b/.test(t);
    const hasMode = /\b(?:view|edit)\s+mode\b/.test(t);
    const hasQuoted = /["'][^"']{1,120}["']/.test(text);
    const hasId = /\(\s*id\s*[:#]?\s*[a-z0-9]+\s*\)/i.test(text);
    if (hasOpen && (hasKind || hasMode || hasQuoted || hasId)) return true;
    if (hasMode && hasKind) return true;
    if (
        /\b(?:view|edit)\s+(?:the\s+)?(?:film|movie|web\s*series|series)\b/.test(
            t
        )
    ) {
        return true;
    }
    return false;
}

export function extractOpenCommand(text: string): SlateOpenCommand | null {
    if (!isOpenContentCommand(text)) return null;
    const raw = text.trim();
    const t = raw.toLowerCase();

    let mode: SlateOpenMode = 'view';
    if (/\bedit\s+mode\b/.test(t) || /\bedit\s+(?:the\s+)?(?:film|movie|series)/.test(t)) {
        mode = 'edit';
    }
    if (/\bview\s+mode\b/.test(t)) {
        mode = 'view';
    }

    let kind: SlateOpenKind = 'unknown';
    if (/\bweb\s*series\b/.test(t) || (/\bseries\b/.test(t) && !/\bfilm\b/.test(t) && !/\bmovie\b/.test(t))) {
        kind = 'webseries';
    } else if (/\b(film|movie)\b/.test(t)) {
        kind = 'film';
    }

    const contentId = extractOpenContentId(raw);
    const title = extractOpenTitle(raw, kind) || (contentId ? contentId : undefined);
    if (!title && !contentId) return null;
    return {
        mode,
        kind,
        title: title || contentId || '',
        contentId,
    };
}

function extractOpenContentId(raw: string): string | undefined {
    const paren = raw.match(/\(\s*id\s*[:#]?\s*([a-zA-Z0-9]+)\s*\)/i);
    if (paren?.[1]) return paren[1];
    const bare = raw.match(/\bid\s*[:#]\s*([a-zA-Z0-9]+)\b/i);
    if (bare?.[1]) return bare[1];
    return undefined;
}

function extractOpenTitle(raw: string, kind: SlateOpenKind): string | undefined {
    const quoted = raw.match(/["']([^"']{1,120})["']/);
    if (quoted?.[1]) return cleanOpenTitle(quoted[1]);

    // open z33 film in view mode
    let m = raw.match(
        /\b(?:open|go\s+to|navigate\s+to|take\s+me\s+to)\s+(?:the\s+)?(.+?)\s+(?:film|movie|web\s*series|series)\b/i
    );
    if (m?.[1]) return cleanOpenTitle(m[1]);

    // open film z33 in view mode / view film z33
    m = raw.match(
        /\b(?:open|view|edit|go\s+to|navigate\s+to)\s+(?:the\s+)?(?:film|movie)\s+(.+?)(?:\s+in\b|$)/i
    );
    if (m?.[1]) return cleanOpenTitle(m[1]);

    m = raw.match(
        /\b(?:open|view|edit|go\s+to|navigate\s+to)\s+(?:the\s+)?(?:web\s*series|series)\s+(.+?)(?:\s+in\b|$)/i
    );
    if (m?.[1]) return cleanOpenTitle(m[1]);

    // open z33 in view mode (no kind word) — last resort
    if (kind === 'unknown') {
        m = raw.match(
            /\b(?:open|go\s+to|navigate\s+to)\s+(?:the\s+)?(.+?)(?:\s+in\s+(?:view|edit)\s+mode\b|$)/i
        );
        if (m?.[1]) return cleanOpenTitle(m[1]);
    }

    return undefined;
}

function cleanOpenTitle(s: string): string | undefined {
    let cleaned = s
        .replace(/["']/g, '')
        .replace(/^(?:please|the)\s+/i, '')
        .replace(/\(\s*id\s*[:#]?\s*[a-zA-Z0-9]+\s*\)/gi, '')
        .replace(/\bid\s*[:#]\s*[a-zA-Z0-9]+\b/gi, '')
        .replace(/\s+/g, ' ')
        .trim();
    cleaned = cleaned
        .replace(/\s+(?:in\s+)?(?:view|edit)(?:\s+mode)?$/i, '')
        .trim();
    cleaned = cleaned
        .replace(/^(?:film|movie|web\s*series|series)\s+/i, '')
        .trim();
    cleaned = cleaned.replace(/\s+how\??$/i, '').trim();
    if (cleaned.length < 1) return undefined;
    if (
        /^(last|this|previous|today|yesterday|from|between|top|how|where|list|dashboard|wallet|report|details?|page|home|settings?|all)\b/i.test(
            cleaned
        )
    ) {
        return undefined;
    }
    return cleaned;
}
